import type { AiPrivacySettings, RedactionKind } from '@mailpilot/shared';
import { EyeOff, FileText, KeyRound, MessageSquareText, ShieldCheck, UserX } from 'lucide-react';
import { fullDate } from '../../lib/format';
import { useSettings, useUpdateSettings } from '../../hooks/useMe';
import { Card, CardBody, CardDivider, CardHeader, Checkbox, ChipInput, Skeleton } from '../ui';
import { Rows, SubSection, SwitchRow } from './SettingsPrimitives';

const REDACTIONS: { kind: RedactionKind; label: string; description: string }[] = [
  { kind: 'card_numbers', label: 'Card numbers', description: 'Payment card numbers are masked before sending.' },
  { kind: 'iban', label: 'Bank account numbers (IBAN)', description: 'IBANs are masked before sending.' },
  { kind: 'phone_numbers', label: 'Phone numbers', description: 'Phone numbers are masked before sending.' },
  { kind: 'national_ids', label: 'National ID numbers', description: 'Social security and similar ID numbers are masked.' },
  { kind: 'passwords', label: 'Passwords & codes', description: 'Text that looks like a password or one-time code is masked.' },
];

const WHAT_IS_SENT = [
  { icon: MessageSquareText, text: 'Only the text of the conversation being drafted or analysed — never your whole mailbox.' },
  { icon: FileText, text: 'Attachments are only sent if you separately allow it below.' },
  { icon: KeyRound, text: 'Content goes to the provider you configure, using your own API key, and is not used by MailPilot to train any general model.' },
  { icon: UserX, text: 'Excluded senders and topics are never sent, and redaction masks sensitive numbers first.' },
];

function useAiPatch() {
  const settings = useSettings();
  const update = useUpdateSettings();
  const patch = (p: Partial<AiPrivacySettings>) => {
    if (!settings) return;
    update.mutate({ ai: { ...settings.ai, ...p } });
  };
  return { ai: settings?.ai, patch };
}

export function AiConsentCard() {
  const { ai, patch } = useAiPatch();
  return (
    <Card>
      <CardHeader
        icon={<ShieldCheck className="h-5 w-5" aria-hidden />}
        title="Your consent"
        description="MailPilot never sends email content to an AI provider until you agree here."
      />
      <CardBody className="!pb-5">
        <p className="mb-3 text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">What is sent, exactly</p>
        <ul className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
          {WHAT_IS_SENT.map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-start gap-2.5 text-[0.8125rem] leading-relaxed text-muted">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-sunken text-muted">
                <Icon className="h-3.5 w-3.5" aria-hidden />
              </span>
              <span>{text}</span>
            </li>
          ))}
        </ul>
      </CardBody>
      <CardDivider />
      <CardBody className="!py-5">
        {!ai ? (
          <Skeleton className="h-16 w-full" />
        ) : (
          <Rows>
            <SwitchRow
              id="ai-consent"
              size="lg"
              checked={!!ai.consentGivenAt}
              onChange={(v) => patch({ consentGivenAt: v ? new Date().toISOString() : null, ...(v ? {} : { attachmentConsentGivenAt: null }) })}
              label="Allow sending email content to my AI provider"
              description={ai.consentGivenAt ? `Consent given ${fullDate(ai.consentGivenAt)}.` : 'Off — AI drafting, analysis and inbox questions are unavailable.'}
            />
            <SwitchRow
              id="ai-attachment-consent"
              checked={!!ai.attachmentConsentGivenAt}
              disabled={!ai.consentGivenAt}
              onChange={(v) => patch({ attachmentConsentGivenAt: v ? new Date().toISOString() : null })}
              label="Also allow sending attachment content"
              description={
                ai.attachmentConsentGivenAt
                  ? `Consent given ${fullDate(ai.attachmentConsentGivenAt)}.`
                  : !ai.consentGivenAt
                    ? 'Turn on email content consent first.'
                    : 'Off — attachments are summarised locally where possible, never sent to AI.'
              }
            />
          </Rows>
        )}
      </CardBody>
    </Card>
  );
}

export function AiPrivacyCard() {
  const { ai, patch } = useAiPatch();
  return (
    <Card>
      <CardHeader icon={<EyeOff className="h-5 w-5" aria-hidden />} title="Privacy, exclusions & redaction" description="Control what may ever reach an AI provider." />
      <CardBody className="space-y-6">
        {!ai ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <>
            <SubSection title="Never send" hint="Conversations with these senders, or mentioning these topics, are kept away from AI entirely.">
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <span className="text-[0.8125rem] font-medium text-ink">Excluded senders</span>
                  <ChipInput label="Excluded senders" value={ai.excludedSenders} onChange={(excludedSenders) => patch({ excludedSenders })} placeholder="name@example.com or @domain.com" />
                  <p className="text-xs text-muted">Addresses or whole domains. Press Enter to add.</p>
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-[0.8125rem] font-medium text-ink">Excluded topics</span>
                  <ChipInput label="Excluded topics" value={ai.excludedTopics} onChange={(excludedTopics) => patch({ excludedTopics })} placeholder="medical, legal…" />
                  <p className="text-xs text-muted">Keywords that mark a thread as off-limits.</p>
                </div>
              </div>
            </SubSection>
            <CardDivider className="!mx-0" />
            <SubSection title="Redact before sending" hint="Masked in the copy that goes to the provider; your mailbox is untouched.">
              <div className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
                {REDACTIONS.map((r) => (
                  <Checkbox
                    key={r.kind}
                    checked={ai.redactPatterns.includes(r.kind)}
                    onChange={(on) => patch({ redactPatterns: on ? [...ai.redactPatterns, r.kind] : ai.redactPatterns.filter((k) => k !== r.kind) })}
                    label={r.label}
                    description={r.description}
                  />
                ))}
              </div>
            </SubSection>
          </>
        )}
      </CardBody>
    </Card>
  );
}
